import { HardwareCheckSchema, type HardwareCheckRequest } from '@cer-autofill/shared';
import {
  HardwareSuggestionSchema,
  type HardwareLabelField,
  StoredReportSchema as LabelReviewedReportSchema,
} from '@cer-autofill/shared';
/**
 * Admin API client. All calls go through the X-Field-Device-Token header,
 * which we self-mint in session.ts (`admin:<name>:<deviceId>`).
 *
 * The /api/admin/* surface is documented in
 * packages/backend/src/routes/admin.ts.
 */

import { z } from 'zod';
import {
  AppUserListFilterSchema,
  AppUserSchema,
  InspectorServiceM8DirectorySchema,
  AvailabilityForecastSchema,
  AvailabilityBlockSchema,
  OrgAvailabilitySettingsSchema,
  IntegrationHealthReportSchema,
  AdminReportStatusSchema,
  AdminReportSummarySchema,
  AdminReportEditResponseSchema,
  DropboxBrowseResponseSchema,
  DropboxSyncCandidatesResponseSchema,
  DropboxSyncImportResultSchema,
  AuditEventSchema,
  CerPortalPayloadV1Schema,
  InspectionJobSchema,
  JobCoverSchema,
  QaReportSchema,
  MissingClausePhotoSchema,
  StoredPhotoManifestSchema,
  StoredReportSchema,
  type PhotoQuarterTurn,
  type FieldHardware,
  type HardwareCheckSelection,
  type AppUser,
  type InspectorServiceM8Directory,
  type InspectorSeedRow,
  type AvailabilityForecast,
  type AvailabilityBlock,
  type OrgAvailabilitySettings,
  type SaveOrgAvailabilitySettings,
  type IntegrationHealthReport,
  type AppUserListFilter,
  type AdminStatusTransition,
  type AdminReportFilter,
  type AdminReportStatus,
  type AdminReportSummary,
  type AdminReportEditRequest,
  type AuditEvent,
  type DropboxAttachRequest,
  type DropboxBrowseResponse,
  type DropboxSyncCandidate,
  type DropboxSyncImportResult,
  type JobFilter,
  type JobTransitionRequest,
  type InspectionJob,
  type JobCover,
  type QaReport,
  type MissingClausePhoto,
  type StoredPhotoManifest,
  type StoredReport,
  type UpdateAppUser,
  type UpsertAppUser,
  ClientSchema,
  type Client,
  type UpsertClient,
  JobsOverviewSchema,
  SchedulingOverviewSchema,
  type JobOverviewFilter,
  type JobsOverview,
  JobMapPointsSchema,
  type JobMapPoints,
  type SchedulingOverview,
  TrancheSchema,
  type Tranche,
  type UpsertTranche,
  RateCardSchema,
  type RateCard,
  type UpsertRateCard,
  BillingRunSchema,
  type BillingRun,
  type BillingLine,
  type BillingJobFactsPatch,
  type TravelClaimPatch,
  UninvoicedJobSchema,
  type UninvoicedJob,
} from '@cer-autofill/shared';
import { backendBaseUrl, log } from './env.js';
import { getSession, updateCognitoTokens } from './session.js';
import { refreshCognitoTokens } from './auth.js';
import { reportReauthRequired } from './lib/auth-diag.js';
import { openFileInNewTab } from './lib/open-file.js';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The photo's manifest exists but its bytes never reached storage — the
 * device minted an upload URL and the PUT failed. Permanent for this photo id
 * (retrying cannot heal it), so the gallery renders a placeholder instead of
 * a retry tile.
 */
export class PhotoMissingError extends Error {
  constructor(photoId: string) {
    super(`Photo ${photoId} never finished uploading`);
    this.name = 'PhotoMissingError';
  }
}

function isPhotoBlobMissing(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 404 &&
    !!error.body?.includes('photo-blob-missing')
  );
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  let res = await sendAuthed(path, init);
  if (res.status === 401) {
    const session = getSession();
    if (session?.refreshToken) {
      await refreshSession(session.refreshToken).catch(() => undefined);
      res = await sendAuthed(path, init);
    }
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, `${res.status} ${res.statusText} on ${path}`, body);
  }
  return res;
}

const REFRESH_MARGIN_MS = 5 * 60_000;

async function sendAuthed(path: string, init: RequestInit = {}): Promise<Response> {
  const session = await sessionWithFreshToken();
  const headers = new Headers(init.headers);
  if (session) headers.set('X-Field-Device-Token', session.deviceToken);
  if (session?.idToken) headers.set('Authorization', `Bearer ${session.idToken}`);
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(`${backendBaseUrl}${path}`, { ...init, headers });
}

export async function sessionWithFreshToken() {
  const session = getSession();
  if (!session?.idToken) return session;
  if (!session.expiresAt || Date.now() < session.expiresAt - REFRESH_MARGIN_MS) return session;
  if (!session.refreshToken) {
    reportReauthRequired('refresh_failed');
    throw new ApiError(401, 'Session expired. Sign in again.');
  }
  try {
    await refreshSession(session.refreshToken);
  } catch (error) {
    reportReauthRequired('refresh_failed');
    throw error;
  }
  const refreshed = getSession();
  if (!refreshed?.idToken) {
    reportReauthRequired('refresh_failed');
    throw new ApiError(401, 'Session refresh failed. Sign in again.');
  }
  return refreshed;
}

export function keepSessionFresh(): () => void {
  const tick = () => {
    if (!navigator.onLine) return;
    const session = getSession();
    if (!session?.refreshToken || !session.expiresAt) return;
    if (Date.now() < session.expiresAt - REFRESH_MARGIN_MS) return;
    void refreshSession(session.refreshToken).catch((error: unknown) =>
      log('background token refresh failed', error),
    );
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') tick();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', tick);
  window.addEventListener('online', tick);
  const timer = window.setInterval(tick, 10 * 60_000);
  tick();
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', tick);
    window.removeEventListener('online', tick);
    window.clearInterval(timer);
  };
}

let refreshInFlight: Promise<void> | null = null;

function refreshSession(refreshToken: string): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshCognitoTokens({ refreshToken })
    .then((result) => {
      updateCognitoTokens({
        idToken: result.IdToken!,
        ...(result.AccessToken !== undefined ? { accessToken: result.AccessToken } : {}),
        ...(result.ExpiresIn !== undefined ? { expiresInSeconds: result.ExpiresIn } : {}),
      });
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * Parse a response body against its shared Zod schema. Throws an ApiError
 * (status 200 — the request succeeded; the payload is what's wrong) when the
 * backend returns something we don't recognise.
 */
async function parseJson<S extends z.ZodTypeAny>(
  res: Response,
  path: string,
  schema: S,
): Promise<z.infer<S>> {
  const json: unknown = await res.json();
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    log('response validation failed', path, parsed.error.issues);
    throw new ApiError(
      res.status,
      `Unexpected response shape from ${path}`,
      JSON.stringify(parsed.error.issues.slice(0, 3)),
    );
  }
  return parsed.data;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Endpoints
 * ──────────────────────────────────────────────────────────────────────────── */

export async function fetchReports(
  filter: Partial<AdminReportFilter>,
): Promise<AdminReportSummary[]> {
  const qs = new URLSearchParams();
  if (filter.inspectorId) qs.set('inspectorId', filter.inspectorId);
  if (filter.status) qs.set('status', filter.status);
  if (filter.fromDate) qs.set('fromDate', filter.fromDate);
  if (filter.toDate) qs.set('toDate', filter.toDate);
  if (filter.limit !== undefined) qs.set('limit', String(filter.limit));
  const path = qs.toString() ? `/api/admin/reports?${qs.toString()}` : '/api/admin/reports';
  const res = await authedFetch(path);
  return parseJson(res, path, z.array(AdminReportSummarySchema));
}

/**
 * List a job's Dropbox folder (or a subfolder of it) for the report screen's
 * Dropbox panel. `path` omitted means the job folder itself.
 */
export async function fetchDropboxFolder(
  jobId: string,
  path?: string,
): Promise<DropboxBrowseResponse> {
  const qs = path ? `?path=${encodeURIComponent(path)}` : '';
  const route = `/api/admin/jobs/${encodeURIComponent(jobId)}/dropbox${qs}`;
  return parseJson(await authedFetch(route), route, DropboxBrowseResponseSchema);
}

/**
 * Object URL for one Dropbox file's bytes (images and PDFs only).
 * Caller owns the URL and must revoke it.
 */
export async function fetchDropboxFileBlobUrl(
  jobId: string,
  path: string,
): Promise<{ url: string; contentType: string }> {
  const route = `/api/admin/jobs/${encodeURIComponent(jobId)}/dropbox/file?path=${encodeURIComponent(path)}`;
  const blob = await (await authedFetch(route, { cache: 'no-store' })).blob();
  return { url: URL.createObjectURL(blob), contentType: blob.type };
}

/** Copy one Dropbox file into the job's photo store under `promptKey`. */
export async function attachDropboxFile(
  jobId: string,
  body: DropboxAttachRequest,
): Promise<StoredPhotoManifest> {
  log('dropbox attach', jobId, body.path, '→', body.promptKey);
  const route = `/api/admin/jobs/${encodeURIComponent(jobId)}/dropbox/attach`;
  const parsed = await parseJson(
    await authedFetch(route, { method: 'POST', body: JSON.stringify(body) }),
    route,
    z.object({ ok: z.literal(true), manifest: StoredPhotoManifestSchema }),
  );
  return parsed.manifest;
}

/**
 * Jobs the "Sync Dropbox" button may import: booked/in-field, no report yet.
 * Whether each is actually importable is decided per job by `importDropboxReport`.
 */
export async function fetchDropboxSyncCandidates(): Promise<DropboxSyncCandidate[]> {
  const path = '/api/admin/reports/dropbox-sync/candidates';
  const res = await authedFetch(path);
  const parsed = await parseJson(res, path, DropboxSyncCandidatesResponseSchema);
  return parsed.candidates;
}

/**
 * Import ONE job's legacy Dropbox report. One job per call by design — each
 * call has a bounded transfer concurrency; the Reports screen loops over the
 * candidate list and shows outcomes as they land.
 */
export async function importDropboxReport(jobId: string): Promise<DropboxSyncImportResult> {
  log('dropbox-sync import', jobId);
  const path = '/api/admin/reports/dropbox-sync/import';
  const res = await authedFetch(path, { method: 'POST', body: JSON.stringify({ jobId }) });
  return parseJson(res, path, DropboxSyncImportResultSchema);
}

export async function fetchReport(jobId: string, submittedAt: string): Promise<StoredReport> {
  const path = `/api/admin/report/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}`;
  const res = await authedFetch(path);
  return parseJson(res, path, StoredReportSchema);
}

export async function fetchPhotos(jobId: string): Promise<StoredPhotoManifest[]> {
  const path = `/api/admin/job/${encodeURIComponent(jobId)}/photos`;
  const res = await authedFetch(path);
  return parseJson(res, path, z.array(StoredPhotoManifestSchema));
}

export async function transitionStatus(
  jobId: string,
  submittedAt: string,
  body: AdminStatusTransition,
): Promise<{ ok: true; status: AdminReportStatus; auditEntries: number }> {
  log('transition', jobId, submittedAt, body);
  const path = `/api/admin/report/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/status`;
  const res = await authedFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return parseJson(
    res,
    path,
    z.object({
      ok: z.literal(true),
      status: AdminReportStatusSchema,
      auditEntries: z.number().int().nonnegative(),
    }),
  );
}

export async function editReport(
  jobId: string,
  submittedAt: string,
  body: AdminReportEditRequest,
): Promise<StoredReport> {
  log('edit-submission', jobId, submittedAt, body.note ?? '(no note)');
  const path = `/api/admin/report/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/edit`;
  const res = await authedFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const parsed = await parseJson(res, path, AdminReportEditResponseSchema);
  return parsed.revision;
}

/**
 * Resolve a URL the caller can drop into `<img src=>`.
 *
 * Prefers a short-lived S3 GET URL (no auth headers needed on `<img>`). Falls
 * back to the authenticated byte proxy + object URL when the store has no
 * presigner (local-fs → 501) or the photo is missing from the signed path.
 *
 * Caller is responsible for revoking blob: object URLs on unmount; https
 * presigned URLs must not be revoked.
 */
export async function fetchPhotoBlobUrl(jobId: string, photoId: string): Promise<string> {
  const signedPath = `/api/admin/job/${encodeURIComponent(jobId)}/photo/${encodeURIComponent(photoId)}/url`;
  try {
    const res = await authedFetch(signedPath, { cache: 'no-store' });
    const data = (await res.json()) as { downloadUrl?: string };
    if (typeof data.downloadUrl === 'string' && data.downloadUrl.length > 0) {
      return data.downloadUrl;
    }
  } catch (error) {
    // The signed path already knows the bytes are gone — the byte proxy would
    // only repeat the same 404.
    if (isPhotoBlobMissing(error)) throw new PhotoMissingError(photoId);
    if (!(error instanceof ApiError) || (error.status !== 501 && error.status !== 404)) {
      // Unexpected failure — still try the byte proxy below.
    }
  }

  return fetchPhotoProxyBlobUrl(jobId, photoId);
}

/**
 * Authenticated byte proxy, bypassing the presigned URL entirely.
 *
 * A presigned URL that the browser then refuses to load — CSP, an expired
 * signature, an S3 permission change — fails inside `<img>`, where the fetch
 * above cannot see it. `PhotoThumb` calls this from `onError` so the tile
 * recovers instead of sitting there broken.
 */
export async function fetchPhotoProxyBlobUrl(jobId: string, photoId: string): Promise<string> {
  const path = `/api/admin/job/${encodeURIComponent(jobId)}/photo/${encodeURIComponent(photoId)}?response=v2`;
  let lastError: unknown;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const res = await authedFetch(path, { cache: 'no-store' });
      const blob = await res.blob();
      if (!blob.type.toLowerCase().startsWith('image/') && blob.type !== 'application/pdf') {
        throw new ApiError(res.status, 'Photo response was not an image or PDF');
      }
      return URL.createObjectURL(blob);
    } catch (error) {
      if (isPhotoBlobMissing(error)) throw new PhotoMissingError(photoId);
      lastError = error;
      const retryable =
        !(error instanceof ApiError) ||
        error.status === 200 ||
        error.status === 429 ||
        error.status >= 500;
      if (!retryable || attempt === 2) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, 300 * 2 ** attempt));
    }
  }

  throw lastError;
}

/* ────────────────────────────────────────────────────────────────────────────
 * M2 — Inspection cases + pre-upload QA
 * (backend/src/routes/admin-jobs.ts; wire types in shared/src/{cases,qa,
 * cer-payload}.ts)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Attach a photo to a report during QA, against the same prompt keys the field
 * tool uses. Persists through the same store as a field capture, so it appears
 * in QA and in the portal payload.
 */

export async function uploadQaPhoto(
  jobId: string,
  input: { photoId: string; promptKey: string; file: File },
): Promise<void> {
  const form = new FormData();
  form.append('photoId', input.photoId);
  form.append('promptKey', input.promptKey);
  form.append('filename', input.file.name);
  form.append('file', input.file);
  const res = await authedFetch(`/api/admin/reports/${encodeURIComponent(jobId)}/photo`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Photo upload failed (${res.status})${body ? `: ${body.slice(0, 200)}` : ''}`);
  }
}

/**
 * Raise ServiceM8 work orders for the given site groups. A two-code group
 * becomes ONE combined work order.
 */
export async function createServiceM8Jobs(groups: string[][]): Promise<{
  created: Array<{ jobIds: string[]; serviceM8JobNumber: string }>;
  skipped: Array<{ jobIds: string[]; reason: string }>;
}> {
  const res = await authedFetch('/api/admin/servicem8/create-jobs', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ groups }),
  });
  if (!res.ok) throw new Error(`ServiceM8 create failed (${res.status})`);
  return (await res.json()) as {
    created: Array<{ jobIds: string[]; serviceM8JobNumber: string }>;
    skipped: Array<{ jobIds: string[]; reason: string }>;
  };
}

export interface ServiceM8FieldChange {
  field: string;
  from: string;
  to: string;
}

export interface ServiceM8PushResult {
  updates: Array<{
    jobId: string;
    serviceM8JobNumber: string;
    changes: ServiceM8FieldChange[];
    applied: boolean;
  }>;
  skipped: Array<{ jobId: string; reason: string }>;
}

/**
 * Carry booking-note, address and badge corrections onto work orders that
 * already exist.
 *
 * `confirm: false` is a pure preview — the backend returns the diff and writes
 * nothing. Callers are expected to show that diff and only pass `confirm:
 * true` once an operator has agreed to it: these descriptions are what
 * inspectors work from in the field.
 */
export async function pushServiceM8Updates(
  jobIds: string[],
  confirm: boolean,
): Promise<ServiceM8PushResult> {
  const res = await authedFetch('/api/admin/servicem8/push-updates', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jobIds, confirm }),
  });
  if (!res.ok) throw new Error(`ServiceM8 update failed (${res.status})`);
  return (await res.json()) as ServiceM8PushResult;
}

/** Response of POST /api/admin/intake/csv (IntakeCsvResult on the backend). */
const IntakeCsvResultSchema = z.object({
  sourceFormat: z.enum(['csv', 'xlsx']),
  clientId: z.string(),
  created: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(z.string()),
  geocoding: z.object({ deferred: z.number().int().nonnegative() }),
  serviceM8: z.object({
    mode: z.literal('preview-only'),
    draftsPrepared: z.number().int().nonnegative(),
    writesEnabled: z.literal(false),
    drafts: z.array(
      z.object({
        action: z.enum(['would-create', 'already-exists', 'configuration-required', 'not-checked']),
        existingJobNumber: z.string().optional(),
        draft: z.object({
          mode: z.literal('preview-only'),
          job: z.object({
            status: z.literal('Work Order'),
            company_uuid: z.string(),
            category_uuid: z.string(),
            purchase_order_number: z.string(),
            job_address: z.string(),
            billing_address: z.string(),
            job_description: z.string(),
          }),
          siteContact: z.object({
            type: z.literal('JOB'),
            first: z.string(),
            last: z.string(),
            email: z.string(),
            mobile: z.string(),
          }),
        }),
      }),
    ),
    matched: z.number().int().nonnegative().default(0),
    combosDetected: z.number().int().nonnegative().default(0),
    // One entry per SITE. `create-combo` is the one that matters: a combined
    // PV+battery visit is a single ServiceM8 work order carrying both codes.
    recommendations: z
      .array(
        z.discriminatedUnion('kind', [
          z.object({
            kind: z.literal('linked'),
            jobIds: z.array(z.string()),
            serviceM8JobNumber: z.string(),
          }),
          z.object({
            kind: z.literal('create-combo'),
            jobIds: z.array(z.string()),
            purchaseOrderNumber: z.string(),
          }),
          z.object({
            kind: z.literal('create-single'),
            jobIds: z.array(z.string()),
            purchaseOrderNumber: z.string(),
          }),
          z.object({
            kind: z.literal('amend-to-combo'),
            jobIds: z.array(z.string()),
            serviceM8JobNumber: z.string(),
            purchaseOrderNumber: z.string(),
          }),
          z.object({
            kind: z.literal('split-across-jobs'),
            jobIds: z.array(z.string()),
            serviceM8JobNumbers: z.array(z.string()),
          }),
        ]),
      )
      .default([]),
    ambiguousSites: z.array(z.string()).default([]),
    clientCollisions: z.array(z.string()).default([]),
    duplicateCodes: z.array(z.string()).default([]),
  }),
});
export type IntakeCsvResult = z.infer<typeof IntakeCsvResultSchema>;

/** Response of POST .../approve-upload. */
const ApproveUploadResultSchema = z.object({
  ok: z.literal(true),
  report: QaReportSchema,
  payload: CerPortalPayloadV1Schema,
  caseTransitioned: z.boolean(),
});
export type ApproveUploadResult = z.infer<typeof ApproveUploadResultSchema>;

export async function fetchCases(filter: Partial<JobFilter>): Promise<InspectionJob[]> {
  const qs = new URLSearchParams();
  if (filter.status) qs.set('status', filter.status);
  if (filter.clientId) qs.set('clientId', filter.clientId);
  if (filter.inspectorId) qs.set('inspectorId', filter.inspectorId);
  if (filter.limit !== undefined) qs.set('limit', String(filter.limit));
  const path = qs.toString() ? `/api/admin/jobs?${qs.toString()}` : '/api/admin/jobs';
  const res = await authedFetch(path);
  return parseJson(res, path, z.array(InspectionJobSchema));
}

export async function fetchCase(jobId: string): Promise<InspectionJob> {
  const path = `/api/admin/jobs/${encodeURIComponent(jobId)}`;
  const res = await authedFetch(path);
  return parseJson(res, path, InspectionJobSchema);
}

/**
 * Guarded status move. The backend answers 409 `{error:'illegal-transition',
 * from, to}` when JOB_TRANSITIONS forbids the hop — the ApiError body
 * carries that JSON (see apiErrorReason).
 */
export async function transitionCase(
  jobId: string,
  body: JobTransitionRequest,
): Promise<InspectionJob> {
  log('case transition', jobId, body);
  const path = `/api/admin/jobs/${encodeURIComponent(jobId)}/transition`;
  const res = await authedFetch(path, { method: 'POST', body: JSON.stringify(body) });
  return parseJson(res, path, InspectionJobSchema);
}

export async function saveBooking(
  jobId: string,
  booking: { inspectorId: string; scheduledDate: string; note?: string },
): Promise<InspectionJob> {
  const path = `/api/admin/jobs/${encodeURIComponent(jobId)}/booking`;
  const res = await authedFetch(path, { method: 'PUT', body: JSON.stringify(booking) });
  return parseJson(res, path, InspectionJobSchema);
}

/**
 * Upload one client CSV or XLSX batch. Without `clientId` the backend auto-detects
 * the feed from the header signature (shared/src/client-feeds.ts).
 */
export async function uploadIntakeFile(file: File, clientId?: string): Promise<IntakeCsvResult> {
  const path = '/api/admin/intake/csv';
  const form = new FormData();
  // Field order matters: @fastify/multipart exposes fields seen BEFORE the
  // file on `file.fields`, so clientId must precede the file part.
  if (clientId) form.append('clientId', clientId);
  form.append('file', file, file.name);
  // authedFetch only forces Content-Type for string bodies — FormData keeps
  // its own boundary header.
  const res = await authedFetch(path, { method: 'POST', body: form });
  return parseJson(res, path, IntakeCsvResultSchema);
}

export async function fetchClients(activeOnly = false): Promise<Client[]> {
  const path = `/api/admin/clients${activeOnly ? '?active=true' : ''}`;
  return parseJson(await authedFetch(path), path, z.array(ClientSchema));
}

export async function saveClient(client: UpsertClient): Promise<Client> {
  const path = `/api/admin/clients/${encodeURIComponent(client.clientId)}`;
  return parseJson(
    await authedFetch(path, { method: 'PUT', body: JSON.stringify(client) }),
    path,
    ClientSchema,
  );
}

export async function fetchJobsOverview(filter: JobOverviewFilter): Promise<JobsOverview> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) if (value) qs.set(key, String(value));
  const path = `/api/admin/jobs-overview${qs.size ? `?${qs.toString()}` : ''}`;
  return parseJson(await authedFetch(path), path, JobsOverviewSchema);
}

export async function fetchJobsMapBaseUrl(): Promise<string> {
  const response = await authedFetch('/api/admin/jobs-map-base');
  return URL.createObjectURL(await response.blob());
}

const GeocodePendingResultSchema = z.object({
  processed: z.number().int().nonnegative(),
  matched: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
});
export type GeocodePendingResult = z.infer<typeof GeocodePendingResultSchema>;

/**
 * Geocode one bounded chunk of jobs that have no coordinates. Intake defers
 * geocoding so the upload can't time out, so this is called repeatedly until
 * `remaining` reaches zero.
 */
export async function geocodePendingJobs(limit = 100): Promise<GeocodePendingResult> {
  const path = '/api/admin/jobs/geocode-pending';
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ limit }) }),
    path,
    GeocodePendingResultSchema,
  );
}

/** Geocoded coordinates for the filtered job set — the map's pin source. */
export async function fetchJobsMapPoints(filter: JobOverviewFilter): Promise<JobMapPoints> {
  const qs = new URLSearchParams(
    Object.entries(filter).filter(([, value]) => value) as [string, string][],
  ).toString();
  const path = qs ? `/api/admin/jobs-map-points?${qs}` : '/api/admin/jobs-map-points';
  return parseJson(await authedFetch(path), path, JobMapPointsSchema);
}

/**
 * MapLibre style descriptor. Its vector source points at our tile proxy, which
 * is unauthenticated so CloudFront can cache it — see
 * {@link mapTileRequestTransform} and the backend's `routes/map-tiles.ts`.
 */
export async function fetchMapStyle(): Promise<unknown> {
  const res = await authedFetch('/api/admin/map-style');
  const style = (await res.json()) as {
    projection?: { type: string };
    sources?: Record<string, { tiles?: string[] }>;
  };
  // Amazon's Standard style ships `projection: globe`, which draws a curved
  // earth edge inside a small dashboard panel. A flat map reads better here.
  style.projection = { type: 'mercator' };
  // The backend emits a root-relative tile template because it can't know the
  // public origin. Make it absolute against the backend we're actually talking
  // to — MapLibre does not fetch the relative form, and in dev the backend is
  // on a different port from the app.
  for (const source of Object.values(style.sources ?? {})) {
    if (source.tiles) {
      source.tiles = source.tiles.map((tile) =>
        tile.startsWith('/') ? `${backendBaseUrl}${tile}` : tile,
      );
    }
  }
  return style;
}

/**
 * MapLibre fetches tiles itself, outside `authedFetch`. Returns undefined for
 * any URL we don't rewrite — sprites and glyphs go straight to Amazon, and
 * current tiles go to `/api/map-tiles/...`, which takes no credentials.
 *
 * Sending auth on a tile request would be actively harmful now: the CloudFront
 * behaviour in front of `/api/map-tiles/*` keys its cache on the URL alone, and
 * an Authorization header on the request stops CloudFront serving from that
 * shared cache — putting every tile back on the Lambda.
 */
export function mapTileRequestTransform(
  url: string,
): { url: string; headers: Record<string, string> } | undefined {
  // MapLibre resolves the style's relative tile template against the *page*
  // origin before calling us, which in dev is the Vite server rather than the
  // backend. Strip any origin and rebuild against backendBaseUrl so the same
  // code works in dev (separate ports) and in production (same host).
  const path = url.replace(/^https?:\/\/[^/]+/, '');
  // Legacy: a style document cached before the tile proxy moved still points at
  // the admin path, which is still authenticated. Drop with the backend alias.
  if (path.startsWith('/api/admin/map-tiles/')) {
    const session = getSession();
    const headers: Record<string, string> = {};
    if (session) headers['X-Field-Device-Token'] = session.deviceToken;
    if (session?.idToken) headers['Authorization'] = `Bearer ${session.idToken}`;
    return { url: `${backendBaseUrl}${path}`, headers };
  }
  if (!path.startsWith('/api/map-tiles/')) return undefined;
  return { url: `${backendBaseUrl}${path}`, headers: {} };
}

export async function fetchSchedulingOverview(
  month: string,
  clientId = '',
  inspectorId = '',
): Promise<SchedulingOverview> {
  const query = new URLSearchParams({ month });
  if (clientId) query.set('clientId', clientId);
  if (inspectorId) query.set('inspectorId', inspectorId);
  const path = `/api/admin/scheduling?${query.toString()}`;
  return parseJson(await authedFetch(path), path, SchedulingOverviewSchema);
}

export async function fetchTranches(activeOnly = false): Promise<Tranche[]> {
  const path = `/api/admin/tranches${activeOnly ? '?active=true' : ''}`;
  return parseJson(await authedFetch(path), path, z.array(TrancheSchema));
}

export async function saveTranche(tranche: UpsertTranche): Promise<Tranche> {
  const path = `/api/admin/tranches/${encodeURIComponent(tranche.trancheId)}`;
  return parseJson(
    await authedFetch(path, { method: 'PUT', body: JSON.stringify(tranche) }),
    path,
    TrancheSchema,
  );
}

export async function fetchRateCards(clientId?: string): Promise<RateCard[]> {
  const path = `/api/admin/rate-cards${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`;
  return parseJson(await authedFetch(path), path, z.array(RateCardSchema));
}

export async function saveRateCard(card: UpsertRateCard): Promise<RateCard> {
  const path = `/api/admin/rate-cards/${encodeURIComponent(card.rateCardId)}`;
  return parseJson(
    await authedFetch(path, { method: 'PUT', body: JSON.stringify(card) }),
    path,
    RateCardSchema,
  );
}

export async function uploadRateCardDocument(rateCardId: string, file: File): Promise<RateCard> {
  const path = `/api/admin/rate-cards/${encodeURIComponent(rateCardId)}/document`;
  const form = new FormData();
  form.append('file', file, file.name);
  return parseJson(await authedFetch(path, { method: 'POST', body: form }), path, RateCardSchema);
}

export async function downloadRateCardDocument(rateCard: RateCard): Promise<void> {
  if (!rateCard.sourceDocument) return;
  const path = `/api/admin/rate-cards/${encodeURIComponent(rateCard.rateCardId)}/document`;
  const blob = await (await authedFetch(path)).blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = rateCard.sourceDocument.fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export async function fetchBillingRuns(clientId?: string): Promise<BillingRun[]> {
  const path = `/api/admin/billing-runs${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`;
  return parseJson(await authedFetch(path), path, z.array(BillingRunSchema));
}

export async function createBillingRun(clientId: string, month: string): Promise<BillingRun> {
  const path = '/api/admin/billing-runs';
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ clientId, month }) }),
    path,
    BillingRunSchema,
  );
}

export async function updateBillingLine(
  billingRunId: string,
  line: BillingLine,
): Promise<BillingRun> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/lines/${encodeURIComponent(line.jobId)}`;
  return parseJson(
    await authedFetch(path, { method: 'PATCH', body: JSON.stringify(line) }),
    path,
    BillingRunSchema,
  );
}

export async function refreshBillingRun(billingRunId: string): Promise<BillingRun> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/refresh`;
  return parseJson(await authedFetch(path, { method: 'POST' }), path, BillingRunSchema);
}

export async function updateBillingJobFacts(
  billingRunId: string,
  jobId: string,
  facts: BillingJobFactsPatch,
): Promise<BillingRun> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/jobs/${encodeURIComponent(jobId)}/billing-facts`;
  return parseJson(
    await authedFetch(path, { method: 'PATCH', body: JSON.stringify(facts) }),
    path,
    BillingRunSchema,
  );
}

export async function updateTravelClaim(
  billingRunId: string,
  travelClaimId: string,
  patch: TravelClaimPatch,
): Promise<BillingRun> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/travel-claims/${encodeURIComponent(travelClaimId)}`;
  return parseJson(
    await authedFetch(path, { method: 'PATCH', body: JSON.stringify(patch) }),
    path,
    BillingRunSchema,
  );
}

export async function acknowledgeBillingWarning(
  billingRunId: string,
  code: string,
  note: string,
): Promise<BillingRun> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/acknowledge`;
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ code, note }) }),
    path,
    BillingRunSchema,
  );
}

export async function approveBillingRun(billingRunId: string): Promise<BillingRun> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/approve`;
  return parseJson(await authedFetch(path, { method: 'POST' }), path, BillingRunSchema);
}

export async function issueBillingRun(
  billingRunId: string,
  invoiceNumber: string,
  invoiceDate: string,
): Promise<BillingRun> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/issue`;
  return parseJson(
    await authedFetch(path, {
      method: 'POST',
      body: JSON.stringify({ invoiceNumber, invoiceDate }),
    }),
    path,
    BillingRunSchema,
  );
}

export async function fetchUninvoiced(): Promise<UninvoicedJob[]> {
  const path = '/api/admin/uninvoiced';
  return parseJson(await authedFetch(path), path, z.array(UninvoicedJobSchema));
}

export async function downloadBillingRunExport(billingRunId: string): Promise<void> {
  const path = `/api/admin/billing-runs/${encodeURIComponent(billingRunId)}/export.csv`;
  const blob = await (await authedFetch(path)).blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${billingRunId}-breakdown.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Run the QA engine for one submission revision. */
export async function fetchQaReport(jobId: string, submittedAt: string): Promise<QaReport> {
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/qa`;
  const res = await authedFetch(path);
  return parseJson(res, path, QaReportSchema);
}

/**
 * QA check + payload assembly — this is what makes a report reachable by the
 * browser extension. Nothing else writes the portal payload, so a report that
 * has not been through here cannot be pushed to CER no matter what status it
 * carries.
 *
 * 409 `{error:'superseded', latestSubmittedAt}` when this is not the newest
 * revision — the payload is stored per job, so approving an older one would
 * overwrite a newer approval and leave the extension filling from stale data.
 * `approveSuperseded:true` proceeds anyway.
 *
 * 409 `{error:'qa-failed'|'qa-warnings', report}` when QA is not clean;
 * `force:true` approves anyway, past warnings **and** failures. QA is advisory
 * — the CER portal validates its own submission and an admin can correct it
 * there — so the override exists, but the caller has to ask for it and the
 * backend records which rules were overridden.
 */
export async function approveForUpload(
  jobId: string,
  submittedAt: string,
  body: {
    force?: boolean;
    approveSuperseded?: boolean;
    missingPhotoOverride?: { acknowledged: boolean; reason: string };
  },
): Promise<ApproveUploadResult> {
  log('approve-upload', jobId, submittedAt, body);
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/approve-upload`;
  const res = await authedFetch(path, { method: 'POST', body: JSON.stringify(body) });
  return parseJson(res, path, ApproveUploadResultSchema);
}

/**
 * The QA report attached to a 409 from approve-upload, or `undefined` for any
 * other failure.
 *
 * The backend answers an unclean QA run with `{error, report}` rather than a
 * bare status, so the caller can show exactly which checks it is being asked
 * to override. Parsed rather than cast — a malformed body should read as "no
 * report", not crash the screen mid-approval.
 */
/**
 * The newest revision's `submittedAt` when approve-upload refused because the
 * one being approved is superseded, or `undefined` for any other failure.
 */
export function supersededByFromApiError(e: unknown): string | undefined {
  if (!(e instanceof ApiError) || e.status !== 409 || !e.body) return undefined;
  try {
    const parsed: unknown = JSON.parse(e.body);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const body = parsed as { error?: unknown; latestSubmittedAt?: unknown };
    if (body.error !== 'superseded') return undefined;
    return typeof body.latestSubmittedAt === 'string' ? body.latestSubmittedAt : undefined;
  } catch {
    return undefined;
  }
}

/** True when approve-upload (or status transition) refused for CER-69 labelling. */
export function labellingUnconfirmedFromApiError(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 409 || !e.body) return false;
  try {
    const parsed: unknown = JSON.parse(e.body);
    if (typeof parsed !== 'object' || parsed === null) return false;
    return (parsed as { error?: unknown }).error === 'labelling-unconfirmed';
  } catch {
    return false;
  }
}

const MissingPhotoOverrideRequiredSchema = z.object({
  error: z.literal('missing-photo-override-required'),
  report: QaReportSchema,
  items: z.array(MissingClausePhotoSchema).min(1),
});

/** The missing-photo gate on a 409 from approve-upload, when that is why it refused. */
export function missingPhotoOverrideFromApiError(
  e: unknown,
): { report: QaReport; items: MissingClausePhoto[] } | undefined {
  if (!(e instanceof ApiError) || e.status !== 409 || !e.body) return undefined;
  try {
    const parsed = MissingPhotoOverrideRequiredSchema.safeParse(JSON.parse(e.body));
    if (!parsed.success) return undefined;
    return { report: parsed.data.report, items: parsed.data.items };
  } catch {
    return undefined;
  }
}

export function qaReportFromApiError(e: unknown): QaReport | undefined {
  if (!(e instanceof ApiError) || e.status !== 409 || !e.body) return undefined;
  try {
    const parsed: unknown = JSON.parse(e.body);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const report = (parsed as { report?: unknown }).report;
    const result = QaReportSchema.safeParse(report);
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Best-effort human reason out of an ApiError body (the backend answers
 * errors as JSON like {error:'illegal-transition', from, to}). Falls back to
 * the error message.
 */
export function apiErrorReason(e: unknown): string {
  if (!(e instanceof ApiError)) {
    return e instanceof Error ? e.message : 'Request failed.';
  }
  if (e.body) {
    try {
      const parsed: unknown = JSON.parse(e.body);
      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>;
        if (typeof obj['error'] === 'string') {
          const extras: string[] = [];
          if (typeof obj['from'] === 'string' && typeof obj['to'] === 'string') {
            extras.push(`${obj['from']} → ${obj['to']}`);
          }
          if (typeof obj['message'] === 'string') extras.push(obj['message']);
          return extras.length > 0 ? `${obj['error']} (${extras.join('; ')})` : obj['error'];
        }
      }
    } catch {
      // fall through to the message
    }
  }
  return e.message;
}

export async function fetchUsers(filter: Partial<AppUserListFilter> = {}): Promise<AppUser[]> {
  const qs = new URLSearchParams();
  if (filter.status) qs.set('status', filter.status);
  if (filter.role) qs.set('role', filter.role);
  if (filter.limit !== undefined) qs.set('limit', String(filter.limit));
  const path = qs.toString() ? `/api/admin/users?${qs.toString()}` : '/api/admin/users';
  const res = await authedFetch(path);
  AppUserListFilterSchema.parse({ ...filter });
  return parseJson(res, path, z.array(AppUserSchema));
}

export async function fetchInspectorServiceM8Directory(): Promise<InspectorServiceM8Directory> {
  const path = '/api/admin/inspectors/servicem8-directory';
  const res = await authedFetch(path);
  return parseJson(res, path, InspectorServiceM8DirectorySchema);
}

export async function fetchAvailabilityForecast(
  from: string,
  to: string,
): Promise<AvailabilityForecast> {
  const query = new URLSearchParams({ from, to });
  const path = `/api/admin/availability?${query.toString()}`;
  return parseJson(await authedFetch(path), path, AvailabilityForecastSchema);
}

export async function createAvailabilityBlock(body: {
  inspectorId?: string;
  start: string;
  end: string;
}): Promise<AvailabilityBlock> {
  const path = '/api/admin/availability/blocks';
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify(body) }),
    path,
    AvailabilityBlockSchema,
  );
}

export async function deleteAvailabilityBlock(blockId: string): Promise<void> {
  const path = `/api/admin/availability/blocks/${encodeURIComponent(blockId)}`;
  await authedFetch(path, { method: 'DELETE' });
}

export async function fetchOrgAvailabilitySettings(): Promise<OrgAvailabilitySettings> {
  const path = '/api/admin/settings/availability';
  return parseJson(await authedFetch(path), path, OrgAvailabilitySettingsSchema);
}

export async function saveOrgAvailabilitySettings(
  body: SaveOrgAvailabilitySettings,
): Promise<OrgAvailabilitySettings> {
  const path = '/api/admin/settings/availability';
  return parseJson(
    await authedFetch(path, { method: 'PUT', body: JSON.stringify(body) }),
    path,
    OrgAvailabilitySettingsSchema,
  );
}

export async function fetchIntegrationHealth(): Promise<IntegrationHealthReport> {
  const path = '/api/admin/settings/integrations';
  return parseJson(await authedFetch(path), path, IntegrationHealthReportSchema);
}

export async function createUser(body: UpsertAppUser): Promise<AppUser> {
  const path = '/api/admin/users';
  const res = await authedFetch(path, { method: 'POST', body: JSON.stringify(body) });
  return parseJson(res, path, AppUserSchema);
}

export async function deleteUser(userId: string): Promise<void> {
  await authedFetch(`/api/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export async function importInspectors(rows: InspectorSeedRow[]): Promise<{
  results: Array<{
    email: string;
    status: 'created' | 'updated' | 'error';
    message?: string | undefined;
  }>;
}> {
  const path = '/api/admin/inspectors/import';
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ rows }) }),
    path,
    z.object({
      results: z.array(
        z.object({
          email: z.string(),
          status: z.enum(['created', 'updated', 'error']),
          message: z.string().optional(),
        }),
      ),
    }),
  );
}

export async function updateUser(userId: string, body: UpdateAppUser): Promise<AppUser> {
  const path = `/api/admin/users/${encodeURIComponent(userId)}`;
  const res = await authedFetch(path, { method: 'PATCH', body: JSON.stringify(body) });
  return parseJson(res, path, AppUserSchema);
}

export async function fetchAudit(limit = 100): Promise<AuditEvent[]> {
  const path = `/api/admin/audit?limit=${encodeURIComponent(String(limit))}`;
  const res = await authedFetch(path);
  return parseJson(res, path, z.array(AuditEventSchema));
}

/**
 * File an uploaded photo against a prompt or clause. Shares the server-side
 * rules with the inspector PWA; only the audit actor differs.
 */
export async function rekeyReportPhoto(
  jobId: string,
  photoId: string,
  promptKey: string,
): Promise<StoredPhotoManifest> {
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/photo/${encodeURIComponent(photoId)}/rekey`;
  const parsed = await parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ promptKey }) }),
    path,
    z.object({
      ok: z.literal(true),
      manifest: StoredPhotoManifestSchema,
      unchanged: z.boolean().optional(),
    }),
  );
  return parsed.manifest;
}

/**
 * Delete a photo during QA. Applies immediately against the photo store (not
 * batched into a report revision). Filed photos are allowed — reviewers need
 * to remove a wrong shot without the field-tool unfile step.
 */
export async function deleteReportPhoto(jobId: string, photoId: string): Promise<void> {
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/photo/${encodeURIComponent(photoId)}`;
  await authedFetch(path, { method: 'DELETE' });
}

/**
 * Turn a stored photo clockwise and save that orientation. The response
 * manifest is the one every other surface should render.
 */
export async function rotateReportPhoto(
  jobId: string,
  photoId: string,
  degrees: PhotoQuarterTurn,
): Promise<StoredPhotoManifest> {
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/photo/${encodeURIComponent(photoId)}/rotate`;
  const parsed = await parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ degrees }) }),
    path,
    z.object({ ok: z.literal(true), manifest: StoredPhotoManifestSchema }),
  );
  return parsed.manifest;
}

/** Every audit event recorded against one job, newest first. */
export async function fetchAuditForJob(jobId: string, limit = 200): Promise<AuditEvent[]> {
  const path = `/api/admin/audit?jobId=${encodeURIComponent(jobId)}&limit=${encodeURIComponent(String(limit))}`;
  const res = await authedFetch(path);
  const events = await parseJson(res, path, z.array(AuditEventSchema));
  return [...events].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

export async function openSignedConsentPdf(jobId: string, submittedAt: string): Promise<void> {
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/signed-consent.pdf`;
  await openFileInNewTab(async () => {
    const response = await authedFetch(path);
    return URL.createObjectURL(await response.blob());
  });
}

export async function openJobSignedConsentPdf(jobId: string): Promise<void> {
  const path = `/api/admin/jobs/${encodeURIComponent(jobId)}/signed-consent.pdf`;
  await openFileInNewTab(async () => {
    const response = await authedFetch(path);
    return URL.createObjectURL(await response.blob());
  });
}

function hardwareSuggestionPath(jobId: string, submittedAt: string) {
  if (submittedAt.startsWith('job:'))
    return `/api/admin/jobs/${encodeURIComponent(jobId)}/hardware-suggestions`;
  return `/api/admin/report/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/hardware-suggestions`;
}
export async function requestHardwareSuggestions(
  jobId: string,
  submittedAt: string,
  inverterIndex: number,
  photoId: string,
) {
  const path = hardwareSuggestionPath(jobId, submittedAt);
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ inverterIndex, photoId }) }),
    path,
    HardwareSuggestionSchema,
  );
}
export async function getHardwareSuggestions(jobId: string, submittedAt: string, id: string) {
  const path = `${hardwareSuggestionPath(jobId, submittedAt)}/${encodeURIComponent(id)}`;
  return parseJson(await authedFetch(path), path, HardwareSuggestionSchema);
}
export async function reviewHardwareSuggestions(
  jobId: string,
  submittedAt: string,
  id: string,
  acceptFields: HardwareLabelField[],
) {
  const path = `${hardwareSuggestionPath(jobId, submittedAt)}/${encodeURIComponent(id)}/review`;
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ acceptFields }) }),
    path,
    z.object({
      suggestion: HardwareSuggestionSchema,
      revision: LabelReviewedReportSchema.optional(),
    }),
  );
}

export async function requestHardwareCheck(
  jobId: string,
  submittedAt: string,
  input: HardwareCheckRequest,
) {
  const path = submittedAt.startsWith('job:')
    ? `/api/admin/jobs/${encodeURIComponent(jobId)}/hardware-checks`
    : `/api/admin/report/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/hardware-checks`;
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify(input) }),
    path,
    HardwareCheckSchema,
  );
}

const JobCoverSaveSchema = z.object({
  job: InspectionJobSchema,
  partnerCopied: z.boolean(),
  partnerConflict: z.boolean(),
  consentCopied: z.boolean(),
});

const JobCoverContextSchema = z.object({
  stored: JobCoverSchema,
  draft: JobCoverSchema,
  seeds: z.array(z.object({ field: z.string(), source: z.string() })),
  submittedAt: z.string().nullable(),
  partnerJobId: z.string().nullable(),
  consentOnJob: z.boolean(),
  consentOnPartner: z.boolean(),
  unresolvedConflicts: z.array(
    z.object({
      id: z.string(),
      path: z.string(),
      label: z.string(),
      dashboardValue: z.unknown(),
      fieldValue: z.unknown(),
    }),
  ),
  portal: z.object({
    noConflictOfInterest: z.boolean(),
    reportReceivedBy: z.string(),
  }),
});
export type JobCoverContext = z.infer<typeof JobCoverContextSchema>;

export async function fetchJobCoverContext(jobId: string): Promise<JobCoverContext> {
  const path = `/api/admin/jobs/${encodeURIComponent(jobId)}/cover`;
  return parseJson(await authedFetch(path), path, JobCoverContextSchema);
}

export async function saveJobCover(job: InspectionJob, cover: JobCover) {
  const path = `/api/admin/jobs/${encodeURIComponent(job.jobId)}/cover`;
  return parseJson(
    await authedFetch(path, {
      method: 'PUT',
      body: JSON.stringify({ cover, expectedUpdatedAt: job.updatedAt }),
    }),
    path,
    JobCoverSaveSchema,
  );
}

export async function publishJobCover(job: InspectionJob) {
  const path = `/api/admin/jobs/${encodeURIComponent(job.jobId)}/cover/publish`;
  return parseJson(
    await authedFetch(path, {
      method: 'POST',
      body: JSON.stringify({ expectedUpdatedAt: job.updatedAt }),
    }),
    path,
    z.object({ ok: z.literal(true), jobIds: z.array(z.string()) }),
  );
}

export async function resolveCoverConflict(
  jobId: string,
  submittedAt: string,
  id: string,
  choice: 'dashboard' | 'field',
) {
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/cover-conflicts/${encodeURIComponent(id)}/resolve`;
  return parseJson(
    await authedFetch(path, { method: 'POST', body: JSON.stringify({ choice }) }),
    path,
    z.object({ ok: z.literal(true) }),
  );
}

export async function saveJobHardware(
  job: InspectionJob,
  hardware: FieldHardware,
  hardwareChecks: HardwareCheckSelection[] = [],
) {
  const path = `/api/admin/jobs/${encodeURIComponent(job.jobId)}/hardware`;
  return parseJson(
    await authedFetch(path, {
      method: 'PUT',
      body: JSON.stringify({ hardware, expectedUpdatedAt: job.updatedAt, hardwareChecks }),
    }),
    path,
    InspectionJobSchema,
  );
}
export async function resolveHardwareConflict(
  jobId: string,
  submittedAt: string,
  id: string,
  choice: 'dashboard' | 'field' | 'edited',
  value?: unknown,
) {
  const path = `/api/admin/reports/${encodeURIComponent(jobId)}/${encodeURIComponent(submittedAt)}/hardware-conflicts/${encodeURIComponent(id)}/resolve`;
  return parseJson(
    await authedFetch(path, {
      method: 'POST',
      body: JSON.stringify({ choice, ...(choice === 'edited' ? { value } : {}) }),
    }),
    path,
    AdminReportEditResponseSchema,
  );
}

export async function fetchJobHardwareContext(jobId: string) {
  const path = `/api/admin/jobs/${encodeURIComponent(jobId)}/hardware`;
  return parseJson(
    await authedFetch(path),
    path,
    z.object({ submittedAt: z.string().nullable(), unresolvedConflictCount: z.number() }),
  );
}
